/**
 * OcrPasteImageModal — 검수 편집기에 이미지가 들어오는 순간, **바꿀 그림이 있을 때만** 묻는다.
 *
 * ── 왜 필요한가 ──
 * 검수자가 이미지를 붙여넣는 동기 하나가 「이 그림이 잘못 잘렸다」다. 그때 필요한 것은 새 그림이
 * 아니라 **그 자리 그림의 교체**(`PATCH …/figures/{f}/{i} {image}` — 이력 950, 원본 bbox 유지)이고,
 * 붙여넣는 순간이 아니면 어느 그림을 바꾸려던 것인지 가릴 수 없다(계약 §10 · 회신 §3).
 *
 * ── 2026-09-04: 세 갈래 → 두 갈래 ──
 * 종전에는 [교체] · [새 그림으로 등록] · [그냥 넣기] 셋이었고, 앞의 둘은 미저장 편집이 있으면
 * 잠겼다. 이제 서버 등록은 **[저장]이 한다**(registerNewFigures) — 「등록」과 「그냥 넣기」는 저장
 * 뒤 같은 상태가 되므로 하나로 합쳤고, 잠기는 선택지는 없다. 교체는 서버 쓰기라 저장하지 않은
 * 편집이 있으면 **호스트가 먼저 저장하고**(onBeforeReplace) 이어서 바꾼다.
 *
 * 바꿀 그림이 없는 자리에서는 호스트가 이 창을 열지 않고 바로 넣는다(OcrReviewModal.takeIncomingImage).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, Plus, Replace } from 'lucide-react';
import { Modal, Button } from '../common';
import {
  fetchFigures, replaceFigureImage, parseImageDataUri, AUTH_REDIRECT,
} from '../FigureEditor/figureApi';
import { describeFigureError } from '../FigureEditor/figureText';

/** 그림 한 장을 눈으로 고르게 하는 썸네일. OCR 은 페이지 크롭(`crop`)이 가장 원본에 가깝다(계약 §3-1). */
function figureThumbSrc(item) {
  if (typeof item?.svg === 'string' && item.svg) return item.svg;
  if (typeof item?.src === 'string' && item.src) return item.src;
  if (typeof item?.crop === 'string' && item.crop) return item.crop;
  return null;
}

export default function OcrPasteImageModal({
  isOpen, problemId, pasted, getAccessToken, onClose, onDone,
  // 2026-09-05 부터 은행도 교체(`PATCH {image}`)를 받는다(계약 §10-2 G1) — 이름은 OCR 이지만 두 화면이 같은 창을 쓴다.
  target = 'ocr',
  /**
   * 교체 직전에 호스트가 본문을 저장할 기회 — `{ ok, saved?, text? }` 를 돌려준다.
   * 저장했으면(saved) 그림 목록과 낙관락이 바뀌었으므로 이 창이 목록을 다시 읽고 같은 그림을 다시 찾는다.
   */
  onBeforeReplace,
}) {
  const field = pasted?.field === 'solution' ? 'solution' : 'body';
  const where = field === 'solution' ? '풀이' : '본문';

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [items, setItems] = useState([]);
  const [token, setToken] = useState(null);   // 낙관락 — 목록 응답의 updated_at
  const [pick, setPick] = useState('new');    // 'new' | { index, src }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const check = useMemo(() => parseImageDataUri(pasted?.src), [pasted?.src]);

  const seqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = (seqRef.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const d = await fetchFigures(target, problemId, getAccessToken);
      if (d === AUTH_REDIRECT || seq !== seqRef.current) return;
      setItems(Array.isArray(d?.items) ? d.items : []);
      setToken(d?.updated_at || null);
    } catch (e) {
      if (seq !== seqRef.current) return;
      setLoadError(describeFigureError(e).text);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [target, problemId, getAccessToken]);

  useEffect(() => {
    if (!isOpen) { setPick('new'); setError(null); setBusy(false); return; }
    load();
  }, [isOpen, load]);

  const mine = useMemo(() => items.filter((it) => it.field === field), [items, field]);

  const run = async () => {
    if (busy) return;
    if (pick === 'new') { onDone?.({ plain: true }); return; }
    setBusy(true);
    setError(null);
    try {
      let index = pick.index;
      let expectedUpdatedAt = token;
      if (typeof onBeforeReplace === 'function') {
        const r = await onBeforeReplace();
        if (r && r !== true && r.ok === false) {
          setError(r.text || '저장하지 못했습니다 — 창을 닫고 [저장]을 눌러 사유를 확인해 주세요.');
          return;
        }
        if (r?.saved) {
          // 저장이 그림을 등록·삭제했을 수 있다 — 목록을 다시 읽어 **같은 그림**을 바이트로 다시 찾는다.
          const d = await fetchFigures(target, problemId, getAccessToken);
          if (d === AUTH_REDIRECT) return;
          const fresh = (Array.isArray(d?.items) ? d.items : []).filter((it) => it.field === field);
          const again = fresh.find((it) => typeof pick.src === 'string' && it.src === pick.src)
            || fresh.find((it) => Number(it.index) === Number(pick.index))
            || null;
          if (!again) {
            setError(`${where} ${pick.index + 1}번째 그림을 다시 찾지 못했습니다 — 창을 닫고 다시 붙여넣어 주세요.`);
            return;
          }
          index = Number(again.index);
          expectedUpdatedAt = d?.updated_at || null;
        }
      }
      const d = await replaceFigureImage(target, problemId, field, index,
        { image: pasted.src, expectedUpdatedAt }, getAccessToken);
      if (d === AUTH_REDIRECT) return;
      // 서버가 편집본의 같은 순번 노드도 갈아 끼웠다(회신 §2 update) — 호스트는 상세만 다시 읽는다.
      onDone?.({ replaced: true, index, editedCopyDropped: d?.edited_copy_dropped === true });
    } catch (e) {
      setError(describeFigureError(e).text);
    } finally {
      setBusy(false);
    }
  };

  const optionClass = (on) => `flex w-full items-center gap-3 rounded-xl border p-2.5 text-left transition-colors ${
    on ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white hover:border-gray-300'}`;
  const replacing = pick !== 'new';

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!busy) onClose?.(); }}
      title="붙여넣은 이미지"
      size="md"
      closeOnBackdropClick={false}
      closeOnEsc={!busy}
      showCloseButton={!busy}
      zIndex={9999986}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3">
          {pasted?.src
            ? <img src={pasted.src} alt="붙여넣은 이미지" className="h-14 w-20 rounded border border-gray-200 bg-white object-contain" />
            : <span className="flex h-14 w-20 items-center justify-center rounded border border-gray-200 bg-white"><ImageIcon className="size-5 text-gray-400" /></span>}
          <div className="min-w-0 text-xs text-gray-600">
            <b className="block text-sm text-gray-900">{where}에 어떻게 넣을까요</b>
            {check.ok && <span className="text-gray-500">{(check.bytes / 1024).toFixed(0)}KB · {check.mime}</span>}
          </div>
        </div>

        {loading && <div className="h-24 rounded-xl bg-gray-100 motion-safe:animate-pulse" />}
        {!loading && loadError && (
          <div className="space-y-2">
            <p role="alert" className="text-xs text-danger">{loadError}</p>
            <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
          </div>
        )}

        {!loading && !loadError && (
          <div className="space-y-1.5">
            {/* ① 새 그림 — 기본이다. 커서 자리에 들어가고 [저장]이 등록한다. */}
            <button type="button" disabled={busy}
              className={`${optionClass(pick === 'new')} disabled:opacity-50`}
              onClick={() => setPick('new')}>
              <span className="flex h-10 w-14 flex-none items-center justify-center rounded border border-gray-200 bg-white">
                <Plus className="size-4 text-gray-400" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-gray-900">새 그림으로 넣기</span>
                <span className="block text-[11px] text-gray-500">커서 자리에 넣습니다 — [저장]하면 그림으로 등록됩니다</span>
              </span>
            </button>

            {/* ② 교체 — 그 자리 그림을 이 이미지로. 원본 bbox 가 남고 이력(950)에 남는다. */}
            {mine.map((it) => {
              const thumb = figureThumbSrc(it);
              const on = replacing && pick.index === it.index;
              return (
                <button key={it.index} type="button" disabled={busy}
                  className={`${optionClass(on)} disabled:opacity-50`}
                  onClick={() => setPick({ index: it.index, src: it.src })}>
                  {thumb
                    ? <img src={thumb} alt="" className="h-10 w-14 flex-none rounded border border-gray-200 bg-white object-contain" />
                    : <span className="flex h-10 w-14 flex-none items-center justify-center rounded border border-gray-200 bg-white text-[10px] text-gray-400">그림</span>}
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-gray-900">{where} {it.index + 1}번째 그림 바꾸기</span>
                    <span className="block text-[11px] text-gray-500">
                      {it.kind === 'svg' ? '선 그림' : '사진'} → 이 이미지로 바꿉니다(이력에 남음)
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {error && <p role="alert" className="rounded-xl border border-danger/30 bg-danger/5 p-2.5 text-xs text-danger">{error}</p>}

        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onClose?.()}>취소</Button>
          <Button size="sm" variant="primary" loading={busy} disabled={busy || (loading && replacing)}
            icon={replacing ? <Replace className="size-3.5" /> : <Plus className="size-3.5" />}
            onClick={run}>
            {replacing ? '교체' : '넣기'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
